import * as React from 'react';
import { ReactElement, RefObject, useRef } from 'react';

import { use$ } from '@legendapp/state/react';
import { Breakpoints, useTheme } from '@volkswagen-onehub/components-core';
import styled from 'styled-components';

import { SliderMagicMixin } from './option-selection-wrapper';

import translate from '../../utils/translate';

import { ContentId, ProductView, TRACKING_TIMER, TrackingEventName } from '../../store/stores/tracking/types';

import {
	degreeImageStore,
	fullscreenStore,
	interiorStore,
	rootStore,
	sliderStore,
	sliderStoreActions
} from '../../store';
import { trackingStore } from '../../store/stores/tracking-store';
import Picker, { PickerType } from './picker';
import Slider from './slider';

interface PickerConfig {
	type: PickerType;
	label: string;
	ariaLabel: string;
	testId: string;
	isVisible: boolean;
	isLoading?: boolean;
	icon?: React.ReactNode;
	ref: RefObject<HTMLButtonElement>;
}

export const PickerSelection = () => {
	const activePicker = use$(sliderStore.activePicker);
	const { variant } = useTheme();
	const isV26 = variant === 'v26';

	const pickerRef = useRef<HTMLDivElement>(null);
	const pickerRefs = useRef<Map<PickerType, RefObject<HTMLButtonElement>>>(new Map());

	const isInteriorMode = use$(interiorStore.isInteriorLoaded);
	const isFullscreenMode = use$(fullscreenStore.isFullscreenMode);
	const isDoubleSliderVisible = use$(sliderStore.isDoubleSliderVisible);
	const interiorLabel = translate('global.interiorLabel');

	const handlePickerSelection = (pickerType: PickerType) => {
		const trackingConfig = {
			[PickerType.Color]: {
				timer: TRACKING_TIMER.PRODUCTVIEW_COLOR_NAME,
				event: TrackingEventName.VISUALIZER_MENU_CLICK
			},
			[PickerType.Rim]: {
				timer: TRACKING_TIMER.PRODUCTVIEW_RIM_NAME,
				event: TrackingEventName.VISUALIZER_MENU_CLICK
			},
			[PickerType.Interior]: {
				timer: TRACKING_TIMER.PRODUCTVIEW_INTERIOR_NAME,
				event: TrackingEventName.VISUALIZER_INTERIOR_LOAD
			}
		};
		const config = trackingConfig[pickerType];
		if (config) {
			trackingStore.setTrackingTimer(config.timer, config.event, {
				contentId: ContentId.MENU,
				sectionName: ProductView.EXTERNAL_VIEW
			});
		}

		sliderStoreActions.setActivePicker(pickerType);
	};

	const getPickerRef = (pickerType: PickerType): RefObject<HTMLButtonElement> => {
		if (!pickerRefs.current.has(pickerType)) {
			pickerRefs.current.set(pickerType, { current: null });
		}
		return pickerRefs.current.get(pickerType)!;
	};

	const pickerConfigs: PickerConfig[] = [
		{
			type: PickerType.Color,
			label: translate('global.colorLabel'),
			ariaLabel: translate('global.ariaLabel.changeColor'),
			testId: 'Menu_ChangeColor',
			isVisible: true,
			isLoading: use$(degreeImageStore.isImageLoaded),
			ref: getPickerRef(PickerType.Color)
		},

		{
			type: PickerType.Rim,
			label: translate('global.rimLabel'),
			ariaLabel: translate('global.ariaLabel.changeRims'),
			testId: 'Menu_ChangeRim',
			isVisible: hasRimOptions(),
			isLoading: use$(degreeImageStore.isImageLoaded),
			ref: getPickerRef(PickerType.Rim)
		},
		{
			type: PickerType.Interior,
			label: interiorLabel,
			ariaLabel: translate('global.ariaLabel.switchToInterior'),
			testId: 'Menu_OpenInterior',
			isVisible: hasInteriorOptions(),
			isLoading: use$(interiorStore.isInteriorLoading),
			ref: getPickerRef(PickerType.Interior)
		}
	];

	if (isInteriorMode) return null;

	const activePickerRef = pickerRefs.current.get(activePicker);

	const visibleConfigs = pickerConfigs.filter(config => config.isVisible && config.type !== PickerType.Interior);
	const interiorConfig = pickerConfigs.find(config => config.type === PickerType.Interior);

	const allVisiblePickers = [...visibleConfigs, ...(interiorConfig?.isVisible ? [interiorConfig] : [])];

	return (
		<PickerWrapper ref={pickerRef} $isDoubleSliderVisible={isDoubleSliderVisible} $isFullscreenMode={isFullscreenMode}>
			<Slider parentRef={pickerRef} activeOptionOrPickerRef={activePickerRef}>
				{isV26 ? (
					<SegmentedGroup>
						{allVisiblePickers.map((config, index) => (
							<Picker
								key={config.type}
								ref={config.ref}
								testId={config.testId}
								ariaLabel={config.ariaLabel}
								label={config.label}
								isActive={activePicker === config.type}
								isLoading={config.type === PickerType.Interior ? false : config.isLoading}
								setActive={() => {
									if (config.type === PickerType.Interior) {
										interiorStore.isInteriorLoaded.set(true);
										return;
									}

									handlePickerSelection(config.type);
								}}
								icon={config.icon as ReactElement}
								isSegmented={true}
								isFirstInSegment={index === 0}
							/>
						))}
					</SegmentedGroup>
				) : (
					<>
						{visibleConfigs.map(config => (
							<Picker
								key={config.type}
								ref={config.ref}
								testId={config.testId}
								ariaLabel={config.ariaLabel}
								label={config.label}
								isActive={activePicker === config.type}
								isLoading={config.isLoading}
								setActive={() => {
									handlePickerSelection(config.type);
								}}
								icon={config.icon as ReactElement}
							/>
						))}
						{interiorConfig?.isVisible && (
							<Picker
								ref={interiorConfig.ref}
								testId={interiorConfig.testId}
								ariaLabel={interiorConfig.ariaLabel}
								label={interiorConfig.label}
								isActive={false}
								isLoading={false}
								setActive={() => {
									interiorStore.isInteriorLoaded.set(true);
								}}
								icon={interiorConfig.icon as ReactElement}
							/>
						)}
					</>
				)}
			</Slider>
		</PickerWrapper>
	);
};

const hasRimOptions = () => {
	const [_, currentModelValue] = use$(rootStore.currentModel);
	return Object.keys(currentModelValue?.thumbnailRim || {}).length > 0;
};

const hasInteriorOptions = () => {
	const [_, currentModelValue] = use$(rootStore.currentModel);
	return Object.keys(currentModelValue?.thumbnailInterior || {}).length > 0;
};

const PickerWrapper = styled.div<{ $isDoubleSliderVisible: boolean; $isFullscreenMode: boolean }>`
	${props => SliderMagicMixin(props.$isDoubleSliderVisible)}
	order: 1;

	// Slider Specific
	// Similar styling as in options-selection, opposite side for fullscreen
	display: flex;
	justify-content: start;
	@media (min-width: ${Breakpoints.b960}px) {
		justify-content: center;
		${({ $isFullscreenMode }) => $isFullscreenMode && 'justify-content: start;'}
	}
`;

const SegmentedGroup = styled.div`
	display: inline-flex;
	align-items: stretch;
	overflow: hidden;
	border: 1px solid #d9cdbd;
	border-radius: 4px;
	background-color: transparent;
`;
